import IconDetail from "@/components/common/IconDetail";
import { ui } from "@/components/ui";
import type { ExtendedEventViewT } from "@/types/database/events";
import type { SizeT } from "@/types/zod/common";
import { Cross } from "lucide-react";
import CardTemplate from "../../../components/common/CardTemplate";
import CardTemplateTitle from "../../../components/common/CardTemplateTitle";
import { buttonVariants } from "../../../components/ui/button";
import { cn } from "../../../lib/utils";
import { useAuth } from "../../../utils/auth";
import EducationItem from "../features/educations/EducationItem";
import SurgeryItem from "../features/surgeries/components/SurgeryItem";
import InjectionItem from "../../injection/components/InjectionItem";
import ShockwaveItem from "../../shockwave/components/ShockwaveItem";
import SelectEventForm from "./SelectEventForm";

interface Props {
	event: ExtendedEventViewT;
	trackerId?: string;
	size?: SizeT;
	showCard?: boolean;
	addable?: boolean;
	className?: string;
	showHeader?: boolean;
	editable?: boolean;
}

const EventCard = ({
	event,
	trackerId,
	size,
	addable = true,
	showCard = true,
	className,
	showHeader = true,
	editable = true,
}: Props) => {
	const auth = useAuth();
	const headerContent = (
		<CardTemplateTitle
			hideDate={false}
			title={
				<IconDetail
					icon={<Cross />}
					words={event.event_name ?? "Intervention"}
				/>
			}
			dateTime={event?.shared_attributes?.reference_date}
		/>
	);

	const bodyContent = (
		<>
			{event.injections_view?.map((i) => (
				<InjectionItem
					event={event}
					trackerId={trackerId}
					key={i.injection_id}
					organisationId={event.organisation_id as string}
					injection={i}
					eventId={event.event_id}
					diaryId={event.diary_id}
					editable={editable}
				/>
			))}
			{event.surgeries_view?.map((s) => (
				<SurgeryItem
					event={event}
					trackerId={trackerId}
					key={s.surgery_id}
					organisationId={event.organisation_id as string}
					surgery={s}
					eventId={event.event_id}
					diaryId={event.diary_id}
					editable={editable}
				/>
			))}
			{event.shockwaves_view?.map((s) => (
				<ShockwaveItem
					event={event}
					trackerId={trackerId}
					key={s.shockwave_id}
					organisationId={event.organisation_id as string}
					shockwave={s}
					eventId={event.event_id}
					diaryId={event.diary_id}
					editable={editable}
				/>
			))}
			{event.educations?.map((e) => (
				<EducationItem
					event={event}
					trackerId={trackerId}
					key={e.education_id}
					organisationId={event.organisation_id as string}
					education={e}
					eventId={event.event_id}
					diaryId={event.diary_id}
					editable={editable}
				/>
			))}
		</>
	);

	const footerContent = auth?.roles?.isPatientOnly ? <></> : (
		<ui.Dialog>
			<ui.DialogTrigger
				className={cn(
					buttonVariants({ size: "sm", variant: "link" }),
					"mx-auto",
				)}
			>
				Add Intervention
			</ui.DialogTrigger>
			<ui.DialogContent>
				<SelectEventForm
					eventId={event.event_id}
					diaryId={event.diary_id}
					organisationId={event.organisation_id as string}
				/>
			</ui.DialogContent>
		</ui.Dialog>
	);

	return (
		<CardTemplate
			size={size}
			className={cn("bg-sky-100 border-sky-300", className)}
			showCard={showCard}
			headerContent={showHeader ? headerContent : <></>}
			bodyContent={bodyContent}
			footerContent={addable ? footerContent : undefined}
		/>
	);
};

export default EventCard;
